/**
 * The shapes every field configuration form is made of.
 *
 * A labelled control, a switch with a label beside it, and the swatch that
 * opens the palette. Small enough to inline and repeated often enough that
 * inlining them would be the thing that makes twenty-seven forms read
 * differently from one another.
 */
import { useState, type ReactNode } from 'react'

import { Label } from '@/shared/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { Switch } from '@/shared/ui/switch'
import {
  SELECT_COLOR_NAMES,
  selectColorStyle,
  type SelectColor,
} from '../../select-options'

export function Setting({
  id,
  label,
  hint,
  children,
}: {
  id: string
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint !== undefined && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

export function Toggle({
  id,
  label,
  hint,
  checked,
  onChange,
  disabled = false,
}: {
  id: string
  label: string
  hint?: string
  checked: boolean
  onChange: (checked: boolean) => void
  /**
   * A switch that cannot be moved is still shown, and still says which way it
   * is set. Hiding it instead would leave nothing to explain why the setting is
   * missing on this field and present on the next one.
   */
  disabled?: boolean
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Switch
          id={id}
          size="sm"
          checked={checked}
          disabled={disabled}
          onCheckedChange={onChange}
        />
        <Label htmlFor={id} className="font-normal">
          {label}
        </Label>
      </div>
      {hint !== undefined && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

/**
 * A swatch that opens the palette.
 *
 * One palette for the whole application: a select option's colour and a
 * rating's symbols come out of the same forty-two names, because they are the
 * same stored thing -- a name that outlives whatever it currently looks like.
 * Upstream keeps a second, four-colour palette for ratings alone.
 */
export function ColourPicker({
  label,
  colour,
  onChange,
}: {
  label: string
  colour: unknown
  onChange: (colour: SelectColor) => void
}) {
  // Controlled so that choosing a colour closes it. A picker left open over the
  // thing it has just changed hides the change.
  const [picking, setPicking] = useState(false)

  return (
    <Popover open={picking} onOpenChange={setPicking}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="size-6 shrink-0 rounded border"
          style={{ backgroundColor: selectColorStyle(colour).backgroundColor }}
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2">
        <div className="grid grid-cols-10 gap-1">
          {SELECT_COLOR_NAMES.map((name) => (
            <button
              key={name}
              type="button"
              aria-label={name}
              aria-pressed={colour === name}
              className={`size-5 rounded border ${
                colour === name ? 'ring-2 ring-ring ring-offset-1' : ''
              }`}
              style={{ backgroundColor: selectColorStyle(name).backgroundColor }}
              onClick={() => {
                setPicking(false)
                onChange(name)
              }}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
